


import { Component, Vue } from "vue-property-decorator";

import { AgentKb } from "App";
import { EventBus } from "App/IOC";
import { Column, PagerSorter } from "App/Models";
import { AgentKbExchange } from "App/Models/AgentKb";

import Table from "Web/Components/Table/Index.vue";

import { CompanyOf, LoadCompanies } from "./Companies";

/* REPONSES IA, PAR UTILISATEUR ( Fred, 26/09/2026 : « un sous-menu a gauche […]
   par exemple par utilisateur » ). A gauche du menu, sous « Réponses IA » : la
   vue generale ( `Index.vue` ), et celle-ci.

   Deux temps : la liste des utilisateurs qui ont interroge l'agent sur la
   periode, du plus actif au moins actif ; un clic, et toutes ses questions.

   D'ou viennent les chiffres :
   - le NOMBRE de questions, de `usage?by=user` — exact, le service l'agrege ;
   - le groupe, la derniere question et les pouces, des 200 derniers echanges
     de la periode : le service ne les agrege pas par utilisateur. Au-dela de
     200 echanges, l'ecran le dit.

   ⚠️ `to` est EXCLUSIF cote service : on envoie le lendemain du dernier jour. */

type Period = 7 | 30 | 90 | 365;

interface UserRow {
    Id: string;
    User: string;
    Group: string;
    Questions: number;
    Last: string;
    LastAt: string;
    Up: number;
    Down: number;
}

interface QuestionRow extends AgentKbExchange {
    Id: number;
    Date: string;
    Question: string;
    Rating: string;
    Article: string;
}

const SAMPLE_LIMIT = 200;

function isoDay( date: Date ): string {
    const month = String( date.getMonth() + 1 ).padStart( 2, "0" );
    const day = String( date.getDate() ).padStart( 2, "0" );

    return `${ date.getFullYear() }-${ month }-${ day }`;
}

@Component( { components: { Table } } )
export default class AgentKbUsersView extends Vue {
    private readonly periods: Period[] = [ 7, 30, 90, 365 ];

    private period: Period = 30;
    private filter: string = "";

    private users: UserRow[] = [];
    private usersLoading: boolean = false;
    private userColumns: Column[] = [];
    private userPager: PagerSorter = new PagerSorter( 50 );
    private sampled: boolean = false;

    /* L'utilisateur ouvert, et ses questions. */
    private selected: UserRow | null = null;
    private questions: QuestionRow[] = [];
    private questionsLoading: boolean = false;
    private questionColumns: Column[] = [];
    private questionPager: PagerSorter = new PagerSorter( 25 );

    private alive: boolean = true;

    /* Le champ filtre la liste deja chargee : un morceau d'adresse suffit ici,
       contrairement au service qui veut l'adresse exacte. */
    private get filteredUsers(): UserRow[] {
        const wanted = this.filter.trim().toLowerCase();

        return ( wanted === "" )
            ? this.users
            : this.users.filter( row => row.User.toLowerCase().indexOf( wanted ) !== -1
                || row.Group === wanted );
    }

    private get pageOfUsers(): UserRow[] {
        const start = ( this.userPager.Page - 1 ) * this.userPager.Limit;

        return this.filteredUsers.slice( start, start + this.userPager.Limit );
    }

    private created(): void {
        this.userColumns = [
            Column.Static( "User", this.$t( "AGENTKB_columnUser" ) as string ),
            Column.Static( "Group", this.$t( "AGENTKB_columnGroup" ) as string ),
            Column.Static( "Questions", this.$t( "AGENTKB_usersQuestions" ) as string ),
            Column.Static( "Votes", this.$t( "AGENTKB_usersVotes" ) as string ),
            Column.Static( "Last", this.$t( "AGENTKB_usersLast" ) as string )
        ];

        this.questionColumns = [
            Column.Static( "Date", this.$t( "AGENTKB_columnDate" ) as string ),
            Column.Static( "Question", this.$t( "AGENTKB_columnQuestion" ) as string ),
            Column.Static( "Rating", this.$t( "AGENTKB_columnRating" ) as string ),
            Column.Static( "Article", this.$t( "AGENTKB_columnArticle" ) as string )
        ];

        this.fetchUsers();
    }

    private beforeDestroy(): void {
        this.alive = false;
    }

    private selectPeriod( period: Period ): void {
        this.period = period;
        this.selected = null;
        this.fetchUsers();
    }

    private onFilterInput(): void {
        this.userPager.Page = 1;
        this.userPager.Total = this.filteredUsers.length;
    }

    private onUserPager( pagerSorter: PagerSorter ): void {
        this.userPager = pagerSorter;
    }

    private openUser( action: string, rows: UserRow[] ): void {
        if( action !== "open" || rows.length === 0 )
            return;

        this.selected = rows[ 0 ];
        this.questionPager.Page = 1;
        this.fetchQuestions();
    }

    private closeUser(): void {
        this.selected = null;
    }

    private onQuestionPager( pagerSorter: PagerSorter ): void {
        this.questionPager = pagerSorter;
        this.fetchQuestions();
    }

    /* Dans la vue generale, filtree sur cet utilisateur : les compteurs, les
       articles, et le volet de chaque echange. */
    private openInGeneral(): void {
        if( this.selected !== null )
            this.$router.push( { path: "/agent-ia", query: { user: this.selected.User } } );
    }

    private range(): { from: string; to: string } {
        const from = new Date();
        const to = new Date();

        from.setDate( from.getDate() - this.period + 1 );
        to.setDate( to.getDate() + 1 );

        return { from: isoDay( from ), to: isoDay( to ) };
    }

    private async fetchUsers(): Promise<void> {
        this.usersLoading = true;

        /* En file : en parallele, chaque appel eveille sa propre instance du
           service ( voir `Index.vue`, `refresh` ). */
        const [ usage, error ] = await AgentKb.Usage( "user", this.range() );
        const [ sample ] = await AgentKb.Exchanges( { ...this.range(), limit: SAMPLE_LIMIT } );

        if( !this.alive )
            return;

        this.usersLoading = false;

        if( error !== undefined || usage === undefined ) {
            if( error !== undefined )
                EventBus.Publish( "Error", error );

            return;
        }

        const items = sample !== undefined ? sample.items : [];

        this.sampled = sample !== undefined && sample.total > SAMPLE_LIMIT;

        this.users = usage.items
            .filter( row => row.cle !== null && row.cle !== "" )
            .map( row => this.toUserRow( String( row.cle ), row.echanges, items ) )
            .sort( ( a, b ) => b.Questions - a.Questions || b.LastAt.localeCompare( a.LastAt ) );

        this.userPager.Page = 1;
        this.userPager.Total = this.filteredUsers.length;

        void LoadCompanies( this.users.map( row => row.Group ) );
    }

    private companyOf( group: string ): string {
        return CompanyOf( group );
    }

    /* Les echanges de l'echantillon arrivent du plus recent au plus ancien : le
       premier trouve est la derniere question. */
    private toUserRow( email: string, count: number, items: AgentKbExchange[] ): UserRow {
        const own = items.filter( item => item.userEmail === email );
        const last = own.length > 0 ? own[ 0 ] : null;

        return {
            Id: email,
            User: email,
            Group: last !== null ? String( last.groupId ) : "—",
            Questions: count,
            Last: last !== null ? this.formatDate( last.createdAt ) : "—",
            LastAt: last !== null ? last.createdAt : "",
            Up: own.filter( item => item.score === 1 ).length,
            Down: own.filter( item => item.score === -1 ).length
        };
    }

    private async fetchQuestions(): Promise<void> {
        if( this.selected === null )
            return;

        this.questionsLoading = true;

        const [ page, error ] = await AgentKb.Exchanges( {
            ...this.range(),
            userEmail: this.selected.User,
            page: this.questionPager.Page,
            limit: this.questionPager.Limit
        } );

        if( !this.alive )
            return;

        this.questionsLoading = false;

        if( error !== undefined || page === undefined ) {
            if( error !== undefined )
                EventBus.Publish( "Error", error );

            return;
        }

        this.questions = page.items.map( item => ( {
            ...item,
            Id: item.id,
            Date: this.formatDate( item.createdAt ),
            Question: item.question,
            Rating: item.score === 1 ? "up" : ( item.score === -1 ? "down" : "" ),
            Article: item.articleTitle || item.articleKbId || "—"
        } ) );

        this.questionPager.Total = page.total;
    }

    private isSelected( row: UserRow ): boolean {
        return this.selected !== null && this.selected.User === row.User;
    }

    private thumbClass( row: QuestionRow ): string {
        return row.score === 1 ? "v-agent-kb__rating--up" : "v-agent-kb__rating--down";
    }

    private periodLabel( period: Period ): string {
        return this.$t( "AGENTKB_period" + period ) as string;
    }

    private formatDate( iso: string ): string {
        const date = new Date( iso );

        if( isNaN( date.getTime() ) )
            return "—";

        return date.toLocaleDateString( "fr-FR", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
        } );
    }
}
